import { notificationHandlers } from "@/lib/notificationHandlers";
import { useGetNotificationsQuery } from "@/redux/feature/notification/notificationApi";
import { useAppSelector } from "@/redux/hooks/hooks";
import { getSocket, initiateSocket } from "@/services/websocket/socket";
import { TNotification } from "@/types/types"; // Ensure this type is defined properly
import { playNotificationSound } from "@/utils/playNotificationSound";
import { pushNotification } from "@/utils/pushNotification";
import {
  connectChatSocket,
  getChatSocket as getChatRealtimeSocket,
} from "@/services/chatSocketService";
import { jwtDecode } from "jwt-decode";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import toast from "react-hot-toast";

export type NotificationCategory = "all" | "unread" | "action_required";

// Define the context type properly
type NotificationContextType = {
  notifications: TNotification[];
  category: NotificationCategory;
  setCategory: React.Dispatch<React.SetStateAction<NotificationCategory>>;
  totalUnreadNotifications: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  page: number;
  meta: {
    page: unknown;
    limit: unknown;
    totalPages: number;
    totalUnreadNotifications: number;
  };
};

type JwtMaybe = {
  userId?: string;
  sub?: string;
  id?: string;
  _id?: string;
};

// Create a NotificationContext with a default empty object
const NotificationContext = createContext<NotificationContextType | null>(null);

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const [notifications, setNotifications] = useState<TNotification[]>([]);

  // console.log(notifications);

  const user = useAppSelector((state) => state.auth);
  const email = user?.user?.email;
  const [category, setCategory] = useState<NotificationCategory>("unread");
  const [page, setPage] = useState(1);

  const { data, refetch } = useGetNotificationsQuery(
    [
      { name: "email", value: email },
      { name: "limit", value: 10 },
      { name: "category", value: category },
      { name: "page", value: page },
    ],
    {
      skip: !email,
    },
  );
  // console.log(data?.data?.data);
  const meta = data?.data?.meta;
  // Update notifications state whenever data changes
  useEffect(() => {
    if (data?.data?.data) {
      // const filteredNotification = dta
      setNotifications(data.data.data);
    }
  }, [data]);

  // Function to check if the tab is active
  const isTabActive = () => !document.hidden;

  const totalUnreadNotifications =
    data?.data?.meta?.totalUnreadNotifications ?? 0;

  // Handle visibility change for checking if tab is active
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.log("Tab is not active.");
      } else {
        console.log("Tab is active.");
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  // Socket connection and notification handling
  useEffect(() => {
    if (!user?.user?.email) {
      return;
    }
    initiateSocket({
      userEmail: user?.user?.email as string,
      token: user.token as string,
    });
    const socket = getSocket();
    socket?.connect();
    if (socket) {
      socket.on("notification", (data: TNotification) => {
        console.log("Received notification:", data);
        refetch();

        playNotificationSound();
        notificationHandlers(data);

        // Show notifications based on tab visibility
        if (!isTabActive()) {
          pushNotification(data.title);
        } else {
          toast.success(data.title);
        }
      });

      // Cleanup when component unmounts
      return () => {
        socket.off("notification");
      };
    }
  }, [user]);

  useEffect(() => {
    if (!user?.token) return;
    let currentUserId = "";
    try {
      const decoded = jwtDecode<JwtMaybe>(user.token as string);
      currentUserId =
        decoded.userId ?? decoded.sub ?? decoded.id ?? decoded._id ?? "";
    } catch {
      return;
    }
    if (!currentUserId) return;

    const chatSocket = connectChatSocket({
      token: user.token as string,
      userId: currentUserId,
    });
    const onIncomingChat = (payload: any) => {
      console.log('🔔 Chat notification received:', payload);
      
      const senderId =
        (payload?.sender_id?._id ?? payload?.sender_id)?.toString?.() ?? "";
      if (senderId && senderId === currentUserId) {
        console.log('❌ Own message, skipping notification');
        return;
      }

      const roomId =
        payload?.room_id ??
        payload?.chat_room_id?.room_id ??
        (typeof payload?.chat_room_id === "string"
          ? payload?.chat_room_id
          : null);
      
      console.log('🔍 Extracted room ID:', roomId);
      
      if (!roomId) {
        console.log('❌ No room ID found, skipping notification');
        return;
      }

      const activeRoom = new URLSearchParams(window.location.search).get(
        "room",
      );
      const onMessagesPage = window.location.pathname.includes("/messages");

      console.log('📍 Current page:', window.location.pathname);
      console.log('📍 Active room:', activeRoom);
      console.log('📍 On messages page:', onMessagesPage);

      // Don't show popup if user is on messaging page viewing this specific room
      if (onMessagesPage && activeRoom && String(activeRoom) === String(roomId)) {
        console.log('✅ User viewing this chat, suppressing popup');
        return;
      }

      console.log('🔔 Showing popup notification');
      playNotificationSound();
      toast.custom(
        (t) => (
          <button
            onClick={() => {
              toast.dismiss(t.id);
              window.location.assign(`/messages?room=${roomId}`);
            }}
            className="w-[300px] rounded-lg border border-[#EAECF0] bg-white p-3 text-left shadow-lg"
          >
            <p className="text-xs text-[#667085]">New message</p>
            <p className="mt-1 line-clamp-2 text-sm font-medium text-[#101828]">
              {payload?.content || "Open chat"}
            </p>
            <p className="mt-2 text-xs text-[#475467]">Click to open chat</p>
          </button>
        ),
        { duration: 7000, position: "bottom-right" },
      );
    };

    chatSocket.on("receiveMessage", onIncomingChat);
    return () => {
      getChatRealtimeSocket()?.off("receiveMessage", onIncomingChat);
    };
  }, [user?.token]);
  return (
    <NotificationContext.Provider
      value={{
        meta,
        page,
        setPage,
        notifications,
        totalUnreadNotifications,
        setCategory,
        category,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

// Custom hook to use notifications
export const useNotifications = () => {
  const context = useContext(NotificationContext);

  if (!context) {
    throw new Error(
      "useNotifications must be used within a NotificationProvider",
    );
  }

  return context;
};
